import { View } from "@-/com.npmjs.react-native";
import { keepPreviousData, useQuery } from "@-/com.npmjs.tanstack__react-query";
import { Surface, SurfaceCollapsibleHeader } from "@-/dev.improve.haniverse.components.ui.surface";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import type { HanziScript } from "@-/dev.improve.haniverse.lib.actor.types";
import {
  selectLearnedPinyinSounds,
  usePronouncedGraphemesQueryOptions,
} from "@-/dev.improve.haniverse.lib.cards.user-card.grapheme.pronunciation.read";
import type { PinyinSoundIntroduction } from "@-/dev.improve.haniverse.lib.level";
import {
  PINYIN_FINAL_ROWS,
  PINYIN_INITIAL_ROWS,
  type PinyinSound,
  type PinyinTone,
  formatPinyinSoundLabel,
  isPinyinSoundLearned,
} from "@-/dev.improve.haniverse.lib.morpheme.grapheme.pinyin";
import {
  useActorToneColors,
  useEffectiveIsToneColorsEnabled,
} from "@-/dev.improve.haniverse.routes.nav.app._lib.actor.user";
import { toneForPinyin } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.grapheme.glyph";
import { useGetGraphemeGlyph } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.grapheme.glyph/client";
import { useIsNavigationTreeFocused } from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs._lib.focus";
import type { ReactNode } from "react";
import {
  PinyinSoundCell,
  PinyinSoundGapCell,
  PinyinToneListItem,
  type PinyinSoundCellCharacter,
} from "#pinyin-sound-cell";
import { usePinyinSoundIntroductionsQuery } from "#use-pinyin-sound-introductions-query";
import {
  type LevelsPackSurface,
  useLevelsPackSurfaceExpanded,
} from "#use-levels-pack-surface-expanded";

/** Every chart is four columns wide: `b p m f`, a final with its i-, u- and ü-series forms. */
const CHART_COLUMN_COUNT = 4;

/** A chart row; `null` is a slot no sound fills. */
type PinyinSoundChartRow = readonly (PinyinSound | null)[];

/** The four full tones, followed by the neutral tone. */
const TONES: readonly PinyinTone[] = [1, 2, 3, 4, 0];

/** The three charts' rows, as sounds. */
function getPinyinSoundChartRows(): Record<PinyinSound["kind"], readonly PinyinSoundChartRow[]> {
  return {
    final: PINYIN_FINAL_ROWS.map((row) =>
      row.map((sound): PinyinSound | null => (sound === null ? null : { kind: "final", sound })),
    ),
    initial: PINYIN_INITIAL_ROWS.map((row) =>
      row.map((sound): PinyinSound => ({ kind: "initial", sound })),
    ),
    tone: TONES.map((sound) => [{ kind: "tone", sound }]),
  };
}

const FINAL_SERIES_HEADINGS = ["Base", "i-series", "u-series", "ü-series"] as const;
/** What each finals column adds in front of its row's base final. */
const FINAL_SERIES_MEDIALS = ["", "i", "u", "ü"] as const;

/** Why a finals chart slot is empty: its row's series has no such final. */
function getFinalGapExplanation(row: PinyinSoundChartRow, column: number): string {
  const base = row[0]?.sound;
  if (base === undefined) {
    return "i, u and ü are finals on their own.";
  }
  return `The final -${FINAL_SERIES_MEDIALS[column] ?? ""}${base} does not exist in Mandarin.`;
}

function renderFinalGap(row: PinyinSoundChartRow, column: number): ReactNode {
  return (
    <PinyinSoundGapCell
      explanation={getFinalGapExplanation(row, column)}
      testID={`pinyin-final-gap-${row[0]?.sound ?? "bare"}-${column}`}
    />
  );
}

/** A chart row padded to the full width, so each sound keeps its column. */
function padChartRow(row: PinyinSoundChartRow): (PinyinSound | null)[] {
  return Array.from({ length: CHART_COLUMN_COUNT }, (_, column) => row[column] ?? null);
}

function getChartRowKey(row: PinyinSoundChartRow): string {
  return row.map((sound) => (sound === null ? "" : formatPinyinSoundLabel(sound))).join(" ");
}

/**
 * A chart's grid. The finals chart fills every empty slot with a disabled cell explaining the gap
 * (`renderGap`); the initials chart's rows simply end.
 */
function PinyinSoundChartRows({
  getCharacter,
  onShowLevel,
  renderGap,
  rows,
}: {
  getCharacter: (sound: PinyinSound) => PinyinSoundCellCharacter | undefined;
  onShowLevel: (introduction: PinyinSoundIntroduction) => void;
  renderGap?: (row: PinyinSoundChartRow, column: number) => ReactNode;
  rows: readonly PinyinSoundChartRow[];
}) {
  return (
    <View className="gap-1">
      {rows.map((row) => (
        <View className="flex-row gap-1" key={getChartRowKey(row)}>
          {/* Padding-free column boxes, so an empty column is exactly as wide as a filled one. */}
          {padChartRow(row).map((sound, column) => (
            <View
              className="min-w-0 flex-1"
              key={sound === null ? `empty-${column}` : formatPinyinSoundLabel(sound)}
            >
              {sound !== null ? (
                <PinyinSoundCell
                  character={getCharacter(sound)}
                  onPress={onShowLevel}
                  sound={sound}
                  testID={`pinyin-${sound.kind}-${sound.sound}`}
                />
              ) : (
                (renderGap?.(row, column) ?? null)
              )}
            </View>
          ))}
        </View>
      ))}
    </View>
  );
}

/**
 * Each chart is its own surface, titled, with how many of its sounds the learner can read. Its
 * header's chevron collapses it to the header, and the device remembers that.
 */
function PinyinSoundChartSurface({
  children,
  getCharacter,
  rows,
  surface,
  testID,
  title,
}: {
  children: ReactNode;
  getCharacter: (sound: PinyinSound) => PinyinSoundCellCharacter | undefined;
  rows: readonly PinyinSoundChartRow[];
  surface: LevelsPackSurface;
  testID: string;
  title: string;
}) {
  const [isExpanded, setIsExpanded] = useLevelsPackSurfaceExpanded(surface);
  const sounds = rows.flat().filter((sound): sound is PinyinSound => sound !== null);
  const characters = sounds.map((sound) => getCharacter(sound));
  // Unknown until the learner's cards load, so the count never flashes zero.
  const isCountKnown = characters.every((character) => character?.isLearned !== undefined);
  const learnedCount = characters.filter((character) => character?.isLearned === true).length;
  const countLabel = isCountKnown ? `${learnedCount} of ${sounds.length} learned` : null;
  return (
    <Surface testID={testID}>
      <SurfaceCollapsibleHeader
        accessibilityLabel={countLabel === null ? title : `${title}, ${countLabel}`}
        className="flex-row items-baseline justify-between gap-2 py-1.5"
        isExpanded={isExpanded}
        onExpandedChange={setIsExpanded}
        testID={`${testID}-header`}
      >
        <Text className="text-sm font-semibold">{title}</Text>
        {countLabel === null ? null : (
          <Text className="text-xs text-muted-foreground tabular-nums">{countLabel}</Text>
        )}
      </SurfaceCollapsibleHeader>
      {isExpanded ? <View className="gap-1 p-1">{children}</View> : null}
    </Surface>
  );
}

/**
 * Every pinyin initial (`b-`), final (`-ia`) and tone (falling), in chart order and on a surface
 * each, beside the first character the curriculum teaches it with (`b-` 本 běn, unlocking at
 * Level 8). Each cell says whether the learner has learned that sound yet — from any reading they
 * study, not only the one shown — so the charts double as a map of the sounds still ahead. A Learn
 * card speaks a reading on reveal, and opens its binder tab, only once every sound of it is learned.
 * Pressing a sound asks for its level (`onShowLevel`).
 */
export function PinyinSoundCharts({
  hanziScript,
  onShowLevel,
}: {
  hanziScript: HanziScript;
  /** Bring the level that introduces a sound into view. */
  onShowLevel: (introduction: PinyinSoundIntroduction) => void;
}) {
  const chartRows = getPinyinSoundChartRows();
  const { data: introductions } = usePinyinSoundIntroductionsQuery(hanziScript);
  // Every enrolled card write changes this read's key; keep the counts while Browse is out of view.
  const isFocused = useIsNavigationTreeFocused();
  const { data: learnedSounds } = useQuery({
    ...usePronouncedGraphemesQueryOptions(),
    enabled: isFocused,
    placeholderData: keepPreviousData,
    select: selectLearnedPinyinSounds,
  });
  const getGraphemeGlyph = useGetGraphemeGlyph();
  const toneColors = useActorToneColors();
  const isToneColorsEnabled = useEffectiveIsToneColorsEnabled();
  const getIntroduction = (sound: PinyinSound): PinyinSoundIntroduction | undefined => {
    switch (sound.kind) {
      case "initial": {
        return introductions?.initials.get(sound.sound);
      }
      case "final": {
        return introductions?.finals.get(sound.sound);
      }
      case "tone": {
        return introductions?.tones.get(sound.sound);
      }
    }
  };
  const getCharacter = (sound: PinyinSound): PinyinSoundCellCharacter | undefined => {
    const introduction = getIntroduction(sound);
    if (introduction === undefined) {
      return undefined;
    }
    const tone = toneForPinyin(introduction.pinyin);
    const glyph = getGraphemeGlyph(introduction.grapheme, {
      // The reading the cell teaches sets the tone color, not the character's main reading.
      color: isToneColorsEnabled && tone !== null ? toneColors[tone] : null,
    });
    // Unknown until the learner's cards load.
    const isLearned =
      learnedSounds === undefined ? undefined : isPinyinSoundLearned(sound, learnedSounds);
    return { glyph, introduction, isLearned };
  };

  return (
    <View className="gap-1" testID="pinyin-sound-charts">
      <PinyinSoundChartSurface
        getCharacter={getCharacter}
        rows={chartRows.tone}
        surface="tones"
        testID="pinyin-tones"
        title="Tones"
      >
        {TONES.map((tone) => (
          <PinyinToneListItem
            character={getCharacter({ kind: "tone", sound: tone })}
            key={tone}
            onPress={onShowLevel}
            tone={tone}
            toneColor={toneColors[tone]}
          />
        ))}
      </PinyinSoundChartSurface>
      <PinyinSoundChartSurface
        getCharacter={getCharacter}
        rows={chartRows.initial}
        surface="initials"
        testID="pinyin-initials"
        title="Initials"
      >
        <PinyinSoundChartRows
          getCharacter={getCharacter}
          onShowLevel={onShowLevel}
          rows={chartRows.initial}
        />
      </PinyinSoundChartSurface>
      <PinyinSoundChartSurface
        getCharacter={getCharacter}
        rows={chartRows.final}
        surface="finals"
        testID="pinyin-finals"
        title="Finals"
      >
        <View className="flex-row gap-1">
          {FINAL_SERIES_HEADINGS.map((heading) => (
            <Text className="flex-1 px-2 text-[11px] text-muted-foreground" key={heading}>
              {heading}
            </Text>
          ))}
        </View>
        <PinyinSoundChartRows
          getCharacter={getCharacter}
          onShowLevel={onShowLevel}
          renderGap={renderFinalGap}
          rows={chartRows.final}
        />
      </PinyinSoundChartSurface>
    </View>
  );
}
